import { useState } from 'react';
import { TextInput, MultiSelect, Textarea } from '@mantine/core';
import { IconTrash, IconPencil, IconCheck, IconX } from '@tabler/icons-react';
import { FlexibleDateInput } from './FlexibleDateInput';
import type { ProcedureLocal } from '../../types/medicalHistory';

interface ProcedureFormProps {
  procedure: ProcedureLocal;
  onChange: (updated: ProcedureLocal) => void;
  onRemove: () => void;
  onSubmit: () => void;
  availableConditions: Array<{ id: string; name: string }>;
  disabled?: boolean;
  isNew?: boolean;
}

// Format date for display
function formatDate(date: ProcedureLocal['procedureDate']): string {
  if (!date) return '';
  const { value, precision } = date;
  if (precision === 'year') return value;
  if (precision === 'month') {
    const [year, month] = value.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
  }
  const d = new Date(value);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ProcedureForm({
  procedure,
  onChange,
  onRemove,
  onSubmit,
  availableConditions,
  disabled = false,
  isNew = false,
}: ProcedureFormProps) {
  const [isEditing, setIsEditing] = useState(isNew);

  const handleChange = (
    field: keyof ProcedureLocal,
    value: string | string[] | ProcedureLocal['procedureDate'] | undefined
  ) => {
    onChange({ ...procedure, [field]: value });
  };

  const handleSave = () => {
    if (procedure.procedureName.trim()) {
      onSubmit();
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      onRemove();
    } else {
      setIsEditing(false);
    }
  };

  const conditionOptions = availableConditions.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  // Get linked condition names for display
  const getLinkedConditionsDisplay = () => {
    if (!procedure.reasonConditionIds?.length) return '';
    return procedure.reasonConditionIds
      .map(id => availableConditions.find(c => c.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  // View mode - display as card
  if (!isEditing) {
    const linkedConditions = getLinkedConditionsDisplay();

    return (
      <div className="card p-4 group">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-medium text-gray-200">{procedure.procedureName}</h4>
            <div className="mt-1 text-sm text-gray-400 space-y-0.5">
              {procedure.procedureDate && (
                <p>{formatDate(procedure.procedureDate)}</p>
              )}
              {linkedConditions && (
                <p>Related to: {linkedConditions}</p>
              )}
              {procedure.comments && (
                <p className="text-gray-500 italic">{procedure.comments}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors disabled:opacity-50"
              title="Edit"
            >
              <IconPencil size={16} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
              title="Delete"
            >
              <IconTrash size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show form
  return (
    <div className="card p-4 border-cyan-500/30">
      <div className="space-y-4">
        {/* Procedure Name */}
        <TextInput
          label="Procedure or Surgery"
          placeholder="e.g., Knee Replacement, Appendectomy, Colonoscopy"
          value={procedure.procedureName}
          onChange={(e) => handleChange('procedureName', e.target.value)}
          disabled={disabled}
          required
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        {/* Date */}
        <FlexibleDateInput
          label="When was this performed?"
          value={procedure.procedureDate}
          onChange={(date) => handleChange('procedureDate', date)}
          disabled={disabled}
        />

        {/* Related Conditions */}
        {conditionOptions.length > 0 && (
          <MultiSelect
            label="Related Conditions"
            placeholder="Select related conditions"
            data={conditionOptions}
            value={procedure.reasonConditionIds || []}
            onChange={(val) => handleChange('reasonConditionIds', val.length > 0 ? val : undefined)}
            disabled={disabled}
            clearable
            searchable
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
              dropdown: 'bg-gray-900 border-gray-700',
              option: 'hover:bg-gray-800 text-gray-200',
              pill: 'bg-cyan-600/30 text-cyan-300',
            }}
          />
        )}

        {/* Comments */}
        <Textarea
          label="Comments"
          placeholder="Share anything helpful — e.g., who performed it, why it was done, any complications or outcomes"
          value={procedure.comments || ''}
          onChange={(e) => handleChange('comments', e.target.value)}
          disabled={disabled}
          minRows={2}
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={disabled}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <IconX size={14} />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || !procedure.procedureName.trim()}
            className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <IconCheck size={14} />
            {isNew ? 'Add Procedure' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
